import React, { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../../config/firebaseConfig";
import { ChevronLeftIcon, EyeIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { Shimmer } from "../../../components/ShimmerComponent";

export default function ViewReceipts() {
  const navigate = useNavigate();

  const [fetching, setFetching] = useState(true);
  const [receipts, setReceipts] = useState<any[]>([]);

  useEffect(() => {
    async function load() {
      try {
        setFetching(true);
        const fn = httpsCallable(functions, "workflowFetchAllUserReports");

        const result = await fn({ action: auth.currentUser?.uid });

        const res = result.data as { count: number; data: any[] };
        setReceipts(res.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setFetching(false);
      }
    }
    load();
  }, []);

  return (
    <div className="min-h-screen bg-dark-background">
      <main className="mx-auto max-w-3xl lg:px-4 sm:px-0 lg:px-8 lg:py-10">

        {/* Mobile Header */}
        <header className="md:hidden sticky top-0 z-40 border-b border-dark-border bg-dark-background backdrop-blur">
          <div className="mx-auto px-4 h-16 flex items-center justify-between">
            <img src="/assets/QID-logo.svg" alt="QID logo" className="h-7" />
          </div>
        </header>

        {/* Card */}
        <div className="rounded-none sm:rounded-2xl px-6 py-6 bg-dark-background-secondary">

          {/* Back + Actions */}
          <div
            className="mb-4 flex items-center gap-2 text-sm text-dark-text cursor-pointer"
            onClick={() => navigate("/")}
          >
            <ChevronLeftIcon className="h-4 w-4" />
            <button type="button">Back to profile</button>
          </div>

          <div className="mb-4 flex items-center justify-between">
            <h1 className="font-semibold text-white text-2xl">
              Payment & receipts
            </h1>

            {fetching && (
              <div className="hidden md:flex items-center">
                <Shimmer className="h-7 w-40" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="mt-6 space-y-4">

            {/* LOADING SHIMMERS */}
            {fetching && (
              <>
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="border border-dark-border bg-dark-background px-4 py-5 rounded-xl"
                  >
                    <Shimmer className="h-5 w-40 mb-3" />
                    <Shimmer className="h-4 w-28 mb-3" />
                    <Shimmer className="h-6 w-20 mb-4" />
                    <Shimmer className="h-9 w-32" />
                  </div>
                ))}
              </>
            )}

            {/* NO DATA */}
            {!fetching && receipts.length === 0 && (
              <div className="text-center text-dark-text py-10">
                No receipts found.
              </div>
            )}

            {/* RECEIPT CARDS */}
            {!fetching &&
  receipts.map((item: any, idx: number) => {
    const amount = Number(item.report?.amount || 0);
    const payments = item.report?.payment || {};
    const paymentValues:any = Object.values(payments); // convert map → array

    // pick latest by createdAt
    paymentValues.sort((a: any, b: any) => b.createdAt?.seconds - a.createdAt?.seconds);

    const receiptUrl = paymentValues[0]?.receiptUrl || null;

    return (
      <div
        key={idx}
        className="border border-dark-border bg-dark-background p-4 sm:p-5 rounded-xl 
                   flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          {/* Title */}
          <div className="text-white font-medium text-lg">
            {item.template?.title || "—"}
          </div>

          {/* Date */}
          <div className="text-dark-text text-sm mt-1">
            {item.report?.createdAt || "—"}
          </div>

          {/* Price */}
          <div className="text-white font-semibold text-xl mt-3">
            ${amount}
          </div>
        </div>

        {/* View Receipt Button only if amount > 0 */}
        {amount > 0 && receiptUrl && (
          <button
            type="button"
            onClick={() => window.open(receiptUrl, "_blank")}
            className="mt-4 sm:mt-0 sm:self-center w-full sm:w-auto 
                       flex items-center justify-center gap-2 text-dark-text
                       bg-dark-background-secondary-medium border border-dark-border 
                       px-4 py-2 rounded-lg hover:bg-dark-background-secondary"
          >
            <EyeIcon className="h-5 w-5" />
            View receipt
          </button>
        )}
      </div>
    );
  })}

          </div>
        </div>
      </main>
    </div>
  );
}
